import * as React from 'react';
import * as typestyle from 'typestyle';
import * as csstips from 'csstips';
import { colors, maxPageContentWidth, maxFormContentWidth, maxDialogContentWidth } from '../base/styles';
import * as gls from '../layout/gls';
import * as text from '../base/text';

export namespace SectionStyles {
  const headerHeight = 80;
  export const islandBackgroundColor = colors.white;

  /**
   * Common across islands
   */
  export const islandBorderColor = colors.warmGrey3;
  export const islandBorderRadius = '3px';
  export const islandBorderWidth = '1px';
  export const islandBorderStyle = {
    border: `${islandBorderWidth} solid ${islandBorderColor}`,
    borderRadius: islandBorderRadius,
  }

  export const rootClassName = typestyle.style(
    csstips.flex,
    csstips.vertical,
    csstips.scroll,
    {
      height: '100%',
      width: '100%'
    }
  );

  export const headerClassName = typestyle.style(
    csstips.content,
    csstips.flexRoot,
    {
      backgroundColor: islandBackgroundColor,
      height: '80px'
    }
  );

  /**
   * Body is like:
   *  {firstMaxWidth} centered
   *    {secondCenterAgain}
   *      {secondMaxWidth}
   */
  export const firstMaxWidthReefClass = typestyle.style(
    csstips.content,
    {
      margin: '0 auto',
      width: '100%',
      maxWidth: `${maxPageContentWidth}px`,
    }
  );

  export const secondCenterAgainIslandClass = typestyle.style(
    csstips.pass,
    csstips.padding(30),
    islandBorderStyle,
    {
      margin: '0 auto',
      width: '100%',
      backgroundColor: islandBackgroundColor,
    }
  );

  export const secondCenterAgainIslandNoBorderClass = typestyle.style(
    csstips.pass,
    csstips.padding(30),
    {
      margin: '0 auto',
      width: '100%',
      backgroundColor: islandBackgroundColor,
    }
  );

  export const secondCenterAgainReefClass = typestyle.style(
    csstips.pass,
    {
      margin: '0 auto',
      width: '100%',
    }
  );

  export const secondMaxWidthClass = typestyle.style(
    csstips.pass,
    {
      margin: '0 auto',
      width: '100%',
      maxWidth: `${maxFormContentWidth}px`,

      backgroundColor: islandBackgroundColor,
    }
  );

  export const dialogIslandClass = typestyle.style(
    csstips.pass,
    csstips.padding(30, 30),
    islandBorderStyle,
    {
      width: '100%',
      maxWidth: `${maxDialogContentWidth}px`,
      margin: '0 auto',
      backgroundColor: islandBackgroundColor,
    }
  );

  export const dialogHeadingReefClass = typestyle.style(
    csstips.padding(30, 30, 10, 30),
    csstips.horizontallyCenterSelf,
    csstips.horizontallyCenterChildren,
    /** The dialog heading is still the same width as the Page */
    csstips.maxWidth(maxPageContentWidth),
  );

  export const dialogHeadingExtraSpaceOnTopClass = typestyle.style(
    typestyle.media({ minWidth: 400 }, { paddingTop: '60px' })
  )
}

/**
 * Puts its children in a padded section with a white background,
 * constraining the width to the max form width
 */
export class FormIsland extends React.Component<{ children?: React.ReactNode }, {}>{
  render() {
    return (
      <div data-comment="HeaderBodyCenteredPage FirstMaxWidth" className={SectionStyles.firstMaxWidthReefClass}>
        <div data-comment="HeaderBodyCenteredPage SecondCenterAgain" className={SectionStyles.secondCenterAgainIslandClass}>
          <div data-comment="HeaderBodyCenteredPage SecondMaxWidth" className={SectionStyles.secondMaxWidthClass}>
            {this.props.children}
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Puts its children in a padded section with a white background,
 * constraining the width to the max page width
 */
export class MaxWidthIsland extends React.Component<{ children?: React.ReactNode }, {}>{
  render() {
    return (
      <div data-comment="HeaderBodyCenteredPage FirstMaxWidth" className={SectionStyles.firstMaxWidthReefClass}>
        <div data-comment="HeaderBodyCenteredPage SecondCenterAgain" className={SectionStyles.secondCenterAgainIslandClass}>
          {this.props.children}
        </div>
      </div>
    );
  }
}

export class MaxWidthReef extends React.Component<{ children?: React.ReactNode }, {}>{
  render() {
    return (
      <div data-comment="HeaderBodyCenteredPage FirstMaxWidth" className={SectionStyles.firstMaxWidthReefClass}>
        <div data-comment="HeaderBodyCenteredPage SecondCenterAgain" className={SectionStyles.secondCenterAgainReefClass}>
          {this.props.children}
        </div>
      </div>
    );
  }
}

export class DialogIsland extends React.Component<{ children?: React.ReactNode }, {}>{
  render() {
    return (
      <div data-comment="DialogIsland" className={SectionStyles.dialogIslandClass}>
        {this.props.children}
      </div>
    );
  }
}


/**
 * Good for dialog headings.
 * Centers its contents and adds aggressive padding
 * The bottom padding is lower to make it closer to the content
 * */
export class DialogHeadingReef extends React.Component<{
  /**
   * If this dialog heading is at the top of the screen
   * we sometimes want it to move even more down, but only on desktop
   */
  extraSpaceOnTop?: boolean;
  children?: React.ReactNode;
}, {}>{
  render() {
    return (
      <div data-comment="DialogHeadingReef"
        className={typestyle.classes(
          SectionStyles.dialogHeadingReefClass,
          this.props.extraSpaceOnTop && SectionStyles.dialogHeadingExtraSpaceOnTopClass
        )}>
        {this.props.children}
      </div>
    );
  }
}


/**
 * - Provides a grid layout with rounded corners and border between each item.
 * - Note: You probably want each list item to have padding + their own vertical centering
 *  Use ListHeader and ListItem for those.
 */
export class ListSection extends React.Component<{ children?: React.ReactNode }, {}>{
  render() {
    const props = this.props;
    const children: any[] = React.Children.toArray(props.children).filter(c => !!c);
    const last = children.length - 1;

    const border = `${SectionStyles.islandBorderWidth} solid ${SectionStyles.islandBorderColor}`;
    const borderRadius = SectionStyles.islandBorderRadius;
    const backgroundColor = SectionStyles.islandBackgroundColor;

    const itemStyle = (index: number): React.CSSProperties => {
      if (index === 0) {
        return {
          borderTop: border,
          borderLeft: border,
          borderRight: border,
          borderBottom: border,
          borderTopLeftRadius: borderRadius,
          borderTopRightRadius: borderRadius,
          backgroundColor,
        }
      }
      else if (index === last) {
        return {
          borderLeft: border,
          borderRight: border,
          borderBottom: border,
          borderBottomLeftRadius: borderRadius,
          borderBottomRightRadius: borderRadius,
          backgroundColor,
        }
      }
      else {
        return {
          borderLeft: border,
          borderRight: border,
          borderBottom: border,
          backgroundColor,
        }
      }
    }

    return (
      <div data-comment="ListContent" className={SectionStyles.firstMaxWidthReefClass}>
        {children.map((c, i) => (<gls.Content key={c.key || i} style={itemStyle(i)}>{c}</gls.Content>))}
      </div>
    );
  }
}

export const ListHeader = ({children}: { children?: React.ReactNode }) =>
  <gls.ContentHorizontalCentered style={{ padding: '24px', color: colors.grey }} children={children} />;

/** If you need a heading inside a listHeader */
export const ListHeaderHeading = text.Heading5;

export const ListItem = ({children}: { children?: React.ReactNode }) =>
  <gls.ContentHorizontalCentered style={{ padding: '24px' }} children={children}/>;

/**
 * Puts its children in a padded section with a white background,
 * constraining the width to the max form width
 */
export class ListForm extends React.Component<{ children?: React.ReactNode }, {}>{
  render() {
    return (
      <div data-comment="HeaderBodyCenteredPage FirstMaxWidth" className={SectionStyles.firstMaxWidthReefClass}>
        <div data-comment="HeaderBodyCenteredPage SecondCenterAgain" className={SectionStyles.secondCenterAgainIslandNoBorderClass}>
          <div data-comment="HeaderBodyCenteredPage SecondMaxWidth" className={SectionStyles.secondMaxWidthClass}>
            {this.props.children}
          </div>
        </div>
      </div>
    );
  }
}
